import { useEffect, useMemo, useState } from 'react'
import { skyApi, type SkyCanvass, type SkyPromo } from '../api/sky'
import { Button, Select, TextField } from './ui'
import { SKY_VERIFICA_COPERTURA_URL, dataIt, dettagliPromo, dividiPromo, etichettaPromo, promoScaduta, type SkyContesto } from '../lib/skyPromo'

/**
 * Campi Sky della pratica che parlano con il canvass PROMO SKY letto dal server: la rete di copertura (verifica manuale su
 * sky.it, con l'indirizzo pronto negli appunti e la regola del canvass) e l'ID promo (menu con le promo compatibili con
 * cosa si attiva, tipo di cliente e rete, più le condizioni dell'offerta sotto il codice scelto).
 */

const MANUALE = '__manuale'

/** Il canvass, letto una volta per pagina (cache in skyApi). */
export function useSkyCanvass(): { canvass: SkyCanvass | null; errore: string | null } {
  const [canvass, setCanvass] = useState<SkyCanvass | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    skyApi.canvassCached()
      .then(c => { if (alive) setCanvass(c) })
      .catch(e => { if (alive) setErrore(e instanceof Error ? e.message : 'Canvass non disponibile') })
    return () => { alive = false }
  }, [])
  return { canvass, errore }
}

const quando = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric' }) : ''

/** Una riga sullo stato del canvass, da mostrare sotto i campi. */
function statoCanvass(c: SkyCanvass): string {
  const base = c.file ? `Canvass ${c.etichetta ?? c.file}${c.lettoIl ? `, letto il ${quando(c.lettoIl)}` : ''}` : 'Canvass non ancora letto'
  if (c.stato === 'errore' && c.errore) return `${base}. Ultimo controllo non riuscito: ${c.errore}`
  return c.controllatoIl ? `${base}, controllato il ${quando(c.controllatoIl)}.` : `${base}.`
}

export function SkyPromoField({ id, label, hint, value, contesto, disabled, onChange }: {
  id: string; label: string; hint?: string | undefined; value: string; contesto: SkyContesto; disabled?: boolean | undefined
  onChange: (value: string) => void
}) {
  const { canvass, errore } = useSkyCanvass()
  const [manuale, setManuale] = useState(false)
  const promo = canvass?.promo
  const { compatibili, altre } = useMemo(
    () => dividiPromo(promo ?? [], contesto),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promo, contesto.tipologia, contesto.tipo, contesto.rete])
  const scelta = (promo ?? []).find(p => p.codice === value)
  const sconosciuto = !!value && !scelta

  // Canvass assente o vuoto: il codice si scrive a mano come prima, con il motivo sotto.
  if (!promo || promo.length === 0) {
    const motivo = errore
      ? `Canvass non disponibile (${errore}).`
      : !canvass
      ? 'Leggo il canvass…'
      : !canvass.configurato
      ? 'Canvass non leggibile: manca la chiave dell\'IA (Impostazioni › IA).'
      : canvass.errore
      ? `Canvass non ancora letto: ${canvass.errore}`
      : 'Canvass non ancora letto dal server.'
    return (
      <div>
        <TextField id={id} label={label} value={value} disabled={disabled} maxLength={20}
          supportingText={`${motivo} ${hint ?? ''}`.trim()} onChange={e => onChange(e.target.value)} />
      </div>
    )
  }

  const aMano = manuale || sconosciuto
  const scaduta = scelta ? promoScaduta(scelta) : false
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <Select id={id} label={label} value={aMano ? MANUALE : value} disabled={disabled}
        onChange={e => {
          if (e.target.value === MANUALE) { setManuale(true); return }
          setManuale(false)
          onChange(e.target.value)
        }}>
        <option value="">—</option>
        {compatibili.length > 0 && (
          <optgroup label={`Promo per questa pratica (${compatibili.length})`}>
            {compatibili.map(p => <option key={p.codice} value={p.codice}>{etichettaPromo(p)}</option>)}
          </optgroup>
        )}
        {altre.length > 0 && (
          <optgroup label={compatibili.length > 0 ? `Altre promo del canvass (${altre.length})` : `Promo del canvass (${altre.length})`}>
            {altre.map(p => <option key={p.codice} value={p.codice}>{etichettaPromo(p)}</option>)}
          </optgroup>
        )}
        <option value={MANUALE}>Altro codice (scrivi a mano)</option>
      </Select>
      {aMano && (
        <div className="mt-2">
          <TextField id={`${id}-manuale`} label="Codice promo scritto a mano" value={value} disabled={disabled} maxLength={20}
            supportingText={sconosciuto && !manuale ? 'Questo codice non è nel canvass in corso: controlla che sia ancora valido.' : 'Non è tra le promo del canvass: il CRM non può mostrarne le condizioni.'}
            onChange={e => onChange(e.target.value)} />
        </div>
      )}
      {scelta && (
        <div className="mt-2 p-2 rounded-2 md-typescale-body-small" style={{ background: 'var(--md-sys-color-surface-container, rgba(0,0,0,.04))' }}
          role="region" aria-label={`Condizioni della promo ${scelta.codice}`}>
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-1">
            <strong>Promo {scelta.codice}</strong>
            {scaduta && <span className="text-danger">Scaduta il {dataIt(scelta.scadenza)}: controlla il canvass nuovo.</span>}
          </div>
          {dettagliPromo(scelta).map(([k, v]) => (
            <div key={k}><span className="text-muted">{k}:</span> {v}</div>
          ))}
        </div>
      )}
      <span className="d-block md-typescale-body-small text-muted mt-1">
        {compatibili.length === 0 && contesto.tipologia === 'MOBILE'
          ? 'Il canvass non ha promo per Sky Mobile. '
          : compatibili.length === 0
          ? 'Nessuna promo del canvass corrisponde a cosa si attiva, al tipo di cliente e alla rete di copertura: controlla quei campi o scegli tra le altre. '
          : ''}
        {statoCanvass(canvass!)}{hint ? ` ${hint}` : ''}
      </span>
    </div>
  )
}

export function SkyCoperturaField({ id, label, hint, value, options, indirizzo, disabled, onChange }: {
  id: string; label: string; hint?: string | undefined; value: string; options: { value: string; label: string }[]
  /** L'indirizzo di attivazione della pratica: finisce negli appunti quando si apre la pagina Sky. */
  indirizzo: string
  disabled?: boolean | undefined
  onChange: (value: string) => void
}) {
  const { canvass } = useSkyCanvass()
  const [copiato, setCopiato] = useState(false)
  async function verifica() {
    if (indirizzo) {
      try { await navigator.clipboard.writeText(indirizzo); setCopiato(true) } catch { /* appunti non disponibili: si copia a mano */ }
    }
    window.open(SKY_VERIFICA_COPERTURA_URL, '_blank', 'noopener,noreferrer')
  }
  const aiuto = copiato
    ? `Indirizzo «${indirizzo}» copiato: incollalo nella pagina Sky e riporta qui la rete.`
    : indirizzo
    ? `Apre la verifica di Sky e copia «${indirizzo}» negli appunti.`
    : 'Apre la verifica di Sky (compila prima l\'indirizzo del cliente per averlo pronto negli appunti).'
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div className="d-flex flex-wrap align-items-end gap-2">
        <div style={{ flex: '1 1 260px' }}>
          <Select id={id} label={label} value={value} disabled={disabled} onChange={e => onChange(e.target.value)}>
            <option value="">—</option>
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </Select>
        </div>
        <Button type="button" variant="tonal" size="sm" icon="open_in_new" onClick={() => void verifica()}>Verifica su sky.it</Button>
      </div>
      <span className="d-block md-typescale-body-small text-muted mt-1">
        {aiuto}{hint ? ` ${hint}` : ''}
        {canvass?.regolaCopertura ? ` Dal canvass: ${canvass.regolaCopertura}` : ''}
      </span>
    </div>
  )
}

export type { SkyPromo }
